import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, query, where, serverTimestamp } from 'firebase/firestore';
import { db, addAuditLog } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import styles from './BranchOrders.module.css';

function QtyInput({ qty, stock, onCommit, className, ariaLabel }) {
  const [localVal, setLocalVal] = useState(String(qty));

  useEffect(() => { setLocalVal(String(qty)); }, [qty]);

  const commit = (val) => {
    const parsed = parseInt(val, 10);
    if (isNaN(parsed) || parsed < 1) { onCommit(null); return; }
    const clamped = Math.min(parsed, stock ?? parsed);
    setLocalVal(String(clamped));
    onCommit(clamped);
  };

  return (
    <input
      className={className}
      type="number"
      min="1"
      max={stock ?? undefined}
      value={localVal}
      onChange={e => setLocalVal(e.target.value)}
      onBlur={e => commit(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter') { e.target.blur(); } }}
      aria-label={ariaLabel}
    />
  );
}

const DELIVERY_FEE = 150;

// WFQ Algorithm — Weighted Fair Queueing
function wfqSort(cartItems) {
  const orderAge = new Date().getHours() / 24;
  return [...cartItems]
    .map(item => ({
      ...item,
      weightScore: item.qty * 0.5 + (item.demandWeight ?? 1) * 0.3 + orderAge * 0.2,
    }))
    .sort((a, b) => b.weightScore - a.weightScore);
}

function groupByCategory(products) {
  const map = {};
  for (const p of products) {
    if (!map[p.category]) map[p.category] = { id: p.category, name: p.category, products: [] };
    map[p.category].products.push(p);
  }
  return Object.values(map)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(cat => ({ ...cat, products: [...cat.products].sort((a, b) => a.name.localeCompare(b.name)) }));
}

export default function BranchOrders({ onNavigate }) {
  const { user }                    = useAuth();
  const [catalog, setCatalog]       = useState([]);
  const [search, setSearch]         = useState('');
  const [cartItems, setCartItems]   = useState([]);
  const [placing, setPlacing]       = useState(false);
  const [expanded, setExpanded]     = useState({});
  const [categoryFilter, setCategoryFilter] = useState('All');

  useEffect(() => {
    async function loadProducts() {
      const q    = query(collection(db, 'products'), where('isArchived', '==', false));
      const snap = await getDocs(q);
      const prods = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCatalog(groupByCategory(prods));
    }
    loadProducts();
  }, []);

  const addToCart = (product, categoryName) => {
    if ((product.stock ?? 0) <= 0) return;
    setCartItems(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) {
        if (existing.qty >= (product.stock ?? 0)) return prev;
        return prev.map(i => i.id === product.id ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...prev, { ...product, category: categoryName, qty: 1 }];
    });
  };

  const changeQty = (id, delta) => {
    setCartItems(prev =>
      prev.map(i => {
        if (i.id !== id) return i;
        const newQty = i.qty + delta;
        if (newQty > (i.stock ?? 0)) return i;
        return { ...i, qty: newQty };
      }).filter(i => i.qty > 0)
    );
  };

  const setQtyDirectly = (id, val) => {
    if (val === null) {
      setCartItems(prev => prev.filter(i => i.id !== id));
      return;
    }
    setCartItems(prev => prev.map(i => i.id === id ? { ...i, qty: val } : i));
  };

  const subtotal = cartItems.reduce((sum, i) => sum + i.price * i.qty, 0);
  const total    = subtotal + (cartItems.length > 0 ? DELIVERY_FEE : 0);

  const handlePlaceOrder = async () => {
    if (cartItems.length === 0 || placing) return;
    const overStock = cartItems.find(i => i.qty > (i.stock ?? 0));
    if (overStock) {
      alert(`"${overStock.name}" only has ${overStock.stock ?? 0} in stock. Please adjust your order.`);
      return;
    }
    setPlacing(true);
    try {
      const sorted    = wfqSort(cartItems);
      const wfqScore  = sorted.reduce((s, i) => s + i.weightScore, 0) / sorted.length;
      const items     = sorted.map(i => ({ productId: i.id, qty: i.qty, price: i.price }));
      const details   = items.map(i => `${i.productId} (${i.qty}x)`).join(', ');

      await addDoc(collection(db, 'orders'), {
        branchId:      user.uid,
        branch:        user.name,
        items,
        details,
        total,
        status:        'Pending',
        paymentMethod: 'Pending',
        wfqScore:      parseFloat(wfqScore.toFixed(4)),
        createdAt:     serverTimestamp(),
        updatedAt:     serverTimestamp(),
      });

      await addAuditLog(user.uid, user.name, `Placed order — ${details}`, 'Ordering');

      setCartItems([]);
      if (onNavigate) onNavigate('transactions');
    } catch (e) {
      console.error('Place order failed:', e);
      alert('Failed to place order. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  const allCategories = ['All', ...catalog.map(cat => cat.name)];

  const filteredCatalog = catalog
    .filter(cat => categoryFilter === 'All' || cat.name === categoryFilter)
    .map(cat => ({
      ...cat,
      products: cat.products.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter(cat => cat.products.length > 0);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Order Stock"
        description="Browse products and place a stock order for your branch"
      />

      <div className={styles.layout}>
        {/* ── Middle Panel: Catalog ── */}
        <div className={styles.catalog}>
          <div className={styles.searchBar}>
            <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className={styles.searchInput}
              type="text"
              placeholder="Search products…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label="Search products"
            />
          </div>

          <div className={styles.categoryFilters}>
            {allCategories.map(cat => (
              <button
                key={cat}
                className={`${styles.categoryPill} ${categoryFilter === cat ? styles.categoryPillActive : ''}`}
                onClick={() => setCategoryFilter(cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          {filteredCatalog.map(cat => (
            <section key={cat.id} className={styles.categorySection}>
              <div className={styles.categoryHeader}>
                <h2 className={styles.categoryTitle}>{cat.name}</h2>
                {cat.products.length > 3 && (
                  <button
                    type="button"
                    className={styles.viewAll}
                    onClick={() => setExpanded(prev => ({ ...prev, [cat.id]: !prev[cat.id] }))}
                  >
                    {expanded[cat.id] ? 'Show Less' : 'View All'}
                  </button>
                )}
              </div>
              <div className={styles.productGrid}>
                {(expanded[cat.id] ? cat.products : cat.products.slice(0, 3)).map(product => (
                  <div key={product.id} className={styles.productCard}>
                    <img
                      src={`/${product.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}.png`}
                      alt={product.name}
                      className={styles.productImage}
                    />
                    <p className={styles.productName}>{product.name}</p>
                    {product.unit && <p className={styles.productUnit}>{product.unit}</p>}
                    <p className={styles.productPrice}>₱{product.price.toLocaleString()}</p>
                    <button
                      className={styles.addToCartBtn}
                      onClick={() => addToCart(product, cat.name)}
                      aria-label={`Add ${product.name} to cart`}
                      disabled={(product.stock ?? 0) <= 0}
                    >
                      {(product.stock ?? 0) <= 0 ? 'Out of Stock' : 'Add to Cart'}
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ))}

          {filteredCatalog.length === 0 && (
            <p className={styles.cartEmpty}>
              {catalog.length === 0 ? 'Loading products…' : `No products match "${search}".`}
            </p>
          )}
        </div>

        {/* ── Right Panel: Cart ── */}
        <aside className={styles.cartPanel} aria-label="Cart">
          <h2 className={styles.cartTitle}>Cart</h2>

          {cartItems.length === 0 ? (
            <p className={styles.cartEmpty}>Your cart is empty.</p>
          ) : (
            <ul className={styles.cartItemList} aria-label="Cart items">
              {cartItems.map(item => (
                <li key={item.id} className={styles.cartItem}>
                  <img
                    src={`/${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}.png`}
                    alt={item.name}
                    className={styles.cartItemImg}
                  />
                  <div className={styles.cartItemInfo}>
                    <p className={styles.cartItemName}>{item.name}</p>
                    <p className={styles.cartItemCategory}>{item.category}{item.unit ? ` · ${item.unit}` : ''}</p>
                    <div className={styles.qtyControls}>
                      <button className={styles.qtyBtn} onClick={() => changeQty(item.id, -1)} aria-label={`Decrease quantity of ${item.name}`}>−</button>
                      <QtyInput
                        className={styles.qtyValue}
                        qty={item.qty}
                        stock={item.stock}
                        onCommit={val => setQtyDirectly(item.id, val, item.stock)}
                        ariaLabel={`Quantity of ${item.name}`}
                      />
                      <button className={styles.qtyBtn} onClick={() => changeQty(item.id, 1)} aria-label={`Increase quantity of ${item.name}`}>+</button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className={styles.orderSummary}>
            <p className={styles.summaryLabel}>Order Summary</p>
            <div className={styles.summaryRow}><span>Subtotal</span><span>₱{subtotal.toLocaleString()}</span></div>
            <div className={styles.summaryRow}><span>Delivery fee</span><span>₱{cartItems.length > 0 ? DELIVERY_FEE.toLocaleString() : 0}</span></div>
            <div className={styles.summaryTotal}><span>Total</span><span>₱{total.toLocaleString()}</span></div>
          </div>

          <button
            className={styles.placeOrderBtn}
            onClick={handlePlaceOrder}
            disabled={cartItems.length === 0 || placing}
          >
            {placing ? 'Placing…' : 'Place Order'}
          </button>
        </aside>
      </div>
    </div>
  );
}
